/**
 * OBC LAN Discovery Service for Web
 * 
 * Detects if OBCs are reachable via local network using mDNS (.local hostname).
 * The OBC publishes itself via Avahi as "obc-name.local".
 * Modern browsers can resolve .local hostnames if the OS supports mDNS:
 *   - macOS: Always works (Bonjour built-in)
 *   - Linux: Works with avahi-daemon + nss-mdns
 *   - Windows: Works with Bonjour installed
 * 
 * Falls back to IP-based heartbeat if .local resolution fails.
 */

// Cache of reachable OBCs: { link_id: { localUrl, reachable, method, lastCheck } }
let lanCache = {};
let listeners = [];

/**
 * Extract OBC name from cloud URL
 * e.g., "https://obc-gucci-museo.cloud.obcevo.com/core/" -> "obc-gucci-museo"
 * @param {string} cloudUrl
 * @returns {string|null}
 */
function extractOBCName(cloudUrl) {
  if (!cloudUrl) return null;
  try {
    const hostname = new URL(cloudUrl).hostname;
    const match = hostname.match(/^([a-zA-Z0-9-]+)\.cloud\.obcevo\.com$/i);
    return match ? match[1].toLowerCase() : null;
  } catch {
    return null;
  }
}

/**
 * Probe an OBC via HTTPS to check if it's reachable.
 * @param {string} url - Full HTTPS URL to probe
 * @param {number} timeout - Timeout in ms (default 3000)
 * @returns {Promise<boolean>} true if reachable
 */
async function probeUrl(url, timeout = 3000) {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);
    await fetch(url, {
      method: 'HEAD',
      mode: 'no-cors',
      signal: controller.signal,
      cache: 'no-store',
    });
    clearTimeout(timer);
    return true;
  } catch {
    return false;
  }
}

/**
 * Try to reach an OBC via .local mDNS hostname.
 * @param {string} obcName - OBC name (e.g., "obc-gucci-museo")
 * @param {number} port - HTTPS port (default 443)
 * @returns {Promise<{reachable: boolean, url: string}>}
 */
async function probeLocal(obcName, port = 443) {
  const portSuffix = port === 443 ? '' : `:${port}`;
  const localUrl = `https://${obcName}.local${portSuffix}/`;
  const reachable = await probeUrl(localUrl);
  return { reachable, url: localUrl };
}

/**
 * Try to reach an OBC via IP (heartbeat fallback).
 * @param {string} ip - Local IP address
 * @param {number} port - HTTPS port (default 443)
 * @returns {Promise<{reachable: boolean, url: string}>}
 */
async function probeIP(ip, port = 443) {
  const portSuffix = port === 443 ? '' : `:${port}`;
  const localUrl = `https://${ip}${portSuffix}/`;
  const reachable = await probeUrl(localUrl);
  return { reachable, url: localUrl };
}

/**
 * Check reachability of all OBCs.
 * Tries .local first, then falls back to IP if available from heartbeat.
 * 
 * @param {Array} links - Array of link objects from API
 * @returns {Promise<Object>} Updated lanCache
 */
async function checkAll(links) {
  if (!links || links.length === 0) {
    lanCache = {};
    notifyListeners();
    return lanCache;
  }

  const results = await Promise.all(
    links.map(async (link) => {
      const obcName = extractOBCName(link.url);
      if (!obcName) return { link, reachable: false, localUrl: null, method: null };

      // 1. Try .local mDNS resolution
      const localResult = await probeLocal(obcName, 443);
      if (localResult.reachable) {
        return { link, reachable: true, localUrl: localResult.url, method: 'mdns' };
      }

      // 2. Fallback to IP from heartbeat (if available)
      if (link.local_ip) {
        const ipResult = await probeIP(link.local_ip, link.local_port || 443);
        if (ipResult.reachable) {
          return { link, reachable: true, localUrl: ipResult.url, method: 'heartbeat' };
        }
      }

      return { link, reachable: false, localUrl: null, method: null };
    })
  );

  const newCache = {};
  for (const { link, reachable, localUrl, method } of results) {
    const obcName = extractOBCName(link.url);
    newCache[link.link_id] = {
      obcName,
      localUrl,
      reachable,
      method, // 'mdns' | 'heartbeat' | null
      lastCheck: Date.now(),
    };
  }

  lanCache = newCache;
  notifyListeners();
  return lanCache;
}

/**
 * Check a single link's LAN reachability
 * @param {Object} link - Link object with url, local_ip, local_port
 * @returns {Promise<Object>} { reachable, localUrl, method }
 */
async function checkOne(link) {
  const obcName = extractOBCName(link.url);
  if (!obcName) return { reachable: false, localUrl: null, method: null };

  // Try .local first
  const localResult = await probeLocal(obcName, 443);
  if (localResult.reachable) {
    const entry = { obcName, localUrl: localResult.url, reachable: true, method: 'mdns', lastCheck: Date.now() };
    lanCache[link.link_id] = entry;
    notifyListeners();
    return entry;
  }

  // Fallback to IP
  if (link.local_ip) {
    const ipResult = await probeIP(link.local_ip, link.local_port || 443);
    if (ipResult.reachable) {
      const entry = { obcName, localUrl: ipResult.url, reachable: true, method: 'heartbeat', lastCheck: Date.now() };
      lanCache[link.link_id] = entry;
      notifyListeners();
      return entry;
    }
  }

  const entry = { obcName, localUrl: null, reachable: false, method: null, lastCheck: Date.now() };
  lanCache[link.link_id] = entry;
  notifyListeners();
  return entry;
}

/**
 * Get cached LAN status for a link
 * @param {string} linkId
 * @returns {Object|null}
 */
function getStatus(linkId) {
  return lanCache[linkId] || null;
}

/**
 * Get all cached statuses
 * @returns {Object}
 */
function getAllStatuses() {
  return { ...lanCache };
}

/**
 * Build the full local URL preserving the path from the cloud URL
 * @param {string} localBaseUrl - Base local URL (e.g., "https://obc-gucci-museo.local/")
 * @param {string} cloudUrl - Original cloud URL to preserve path
 * @returns {string}
 */
function buildLocalUrl(localBaseUrl, cloudUrl) {
  try {
    const cloudParsed = new URL(cloudUrl);
    const localParsed = new URL(localBaseUrl);
    return `${localParsed.origin}${cloudParsed.pathname}${cloudParsed.search}${cloudParsed.hash}`;
  } catch {
    return localBaseUrl;
  }
}

/**
 * Subscribe to LAN status changes
 * @param {Function} listener
 * @returns {Function} unsubscribe function
 */
function subscribe(listener) {
  listeners.push(listener);
  return () => {
    listeners = listeners.filter(l => l !== listener);
  };
}

function notifyListeners() {
  const snapshot = { ...lanCache };
  listeners.forEach(fn => {
    try { fn(snapshot); } catch (e) { console.error('[LANDiscovery] Listener error:', e); }
  });
}

/**
 * Clear all cache
 */
function clear() {
  lanCache = {};
  notifyListeners();
}

const OBCLanDiscovery = {
  extractOBCName,
  probeUrl,
  probeLocal,
  probeIP,
  checkAll,
  checkOne,
  getStatus,
  getAllStatuses,
  buildLocalUrl,
  subscribe,
  clear,
};

export default OBCLanDiscovery;
